<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import { useRoute } from "vue-router";
import { computed } from "vue";
import { useTermsDocument, useTermVersion } from "@/composables/models/terms/useTerms";
import { useSettings } from "@/composables/shared/useSettings";

definePage({
  meta: {
    layout: "public",
    requiresAuth: true,
  },
});

const route = useRoute("/documents/[id]");
const { isDarkMode } = useSettings();
const id = computed(() => {
  const p = route.params.id;
  return Array.isArray(p) ? p[0] : p;
});
const versionParam = computed(() => {
  const v = route.query.version;
  return v ? Number(v) : null;
});

const { data: document, isLoading: isLoadingDocument } = useTermsDocument(id);
const { data: specificVersion, isLoading: isLoadingVersion } = useTermVersion(
  computed(() => ({
    doc_pk: id.value,
    id: versionParam.value ?? undefined,
  })),
  computed(() => versionParam.value !== null)
);

const isLoading = computed(() => isLoadingDocument.value || isLoadingVersion.value);
const raw = computed(() => {
  if (versionParam.value !== null) return specificVersion.value?.content ?? "";
  return document.value?.current_version?.content ?? "";
});

const parsed = computed(() => {
  const input = raw.value;
  if (!input.trim()) return { body: "", styles: "" };
  const doc = new DOMParser().parseFromString(input, "text/html");
  const styles = Array.from(doc.querySelectorAll("style"))
    .map((s) => s.textContent ?? "")
    .join("\n")
    .trim();
  return { body: doc.body.innerHTML, styles };
});

const scopedStyles = computed(() => {
  if (!parsed.value.styles) return "";

  // 1. Scope every selector to .terms-document-view
  // Map `body` to the container itself since there is no <body> inside the div
  const withBodyMapped = parsed.value.styles.replace(
    /(^|[\s}])(body)\s*\{/gi,
    (_match, p1) => `${p1} .terms-document-view {`
  );
  const scoped = withBodyMapped.replace(
    /(^|[\s}])([a-z0-9\s,.\-#_*\[\]"'=^$~|>+~:()]+)\s*\{/gi,
    (match, p1, p2) => {
      if (/^(@|:root|\.terms-document-view)/.test(p2.trim())) return match;
      return `${p1} .terms-document-view ${p2.trim()} {`;
    }
  );

  // 2. Force every declaration to win over app styles, except background on the container itself
  const withImportant = scoped.replace(/:\s*([^;{}]+?)\s*(?:!important)?\s*;/g, ": $1 !important;");
  return withImportant + "\n.terms-document-view { background: transparent !important; }";
});

const darkDocumentOverrides = computed(() => {
  if (!isDarkMode.value) return "";

  return `
.terms-document-view,
.terms-document-view :is(h1, h2, h3, h4, h5, h6, p, li, td, th, span, div, blockquote) {
  color: rgb(var(--v-theme-on-surface)) !important;
  background-color: transparent !important;
}
.terms-document-view a {
  color: rgb(var(--v-theme-secondary)) !important;
}
.terms-document-view :is(table, td, th) {
  border-color: rgb(var(--v-theme-on-surface) / 0.12) !important;
}
.terms-document-view hr {
  border-color: rgb(var(--v-theme-on-surface) / 0.08) !important;
}
`;
});

const documentViewStyles = computed(() => {
  const resetRule = isDarkMode.value ? "" : ".terms-document-view * { all: revert !important; }\n";
  return `${resetRule}${scopedStyles.value}${darkDocumentOverrides.value}`;
});

const closeTab = () => window.close();
</script>

<template>
  <div class="tw:flex tw:flex-col tw:gap-4 tw:pt-4 tw:pb-6 tw:h-[calc(100dvh-78px)]">
    <div class="tw:flex tw:items-center tw:justify-between tw:gap-4">
      <h1 class="tw:text-2xl tw:md:text-4xl tw:font-bold tw:tracking-tight tw:text-on-surface">
        {{ document?.title ?? "" }}
        <span
          v-if="versionParam"
          class="tw:text-base tw:font-mono tw:text-on-surface/50 tw:ml-2"
          >v{{ versionParam }}</span
        >
      </h1>
      <v-btn
        icon
        rounded="lg"
        color="secondary"
        aria-label="Close"
        @click="closeTab"
      >
        <v-icon icon="mdi-close" />
      </v-btn>
    </div>
    <div
      class="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-hidden tw:rounded-2xl tw:border tw:border-on-surface/10 tw:bg-surface"
    >
      <v-skeleton-loader
        v-if="isLoading"
        type="article"
        class="tw:p-6"
      />
      <template v-else-if="parsed.body">
        <!-- Reset app styles inside the document container, then apply document's own styles on top -->
        <component
          is="style"
          type="text/css"
        >
          {{ documentViewStyles }}
        </component>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          class="terms-document-view tw:flex-1 tw:overflow-y-auto tw:p-6"
          v-html="parsed.body"
        />
      </template>
    </div>
  </div>
</template>

<style scoped>
.terms-document-view {
  font-size: 0.9375rem;
  line-height: 1.8;
  color: rgb(var(--v-theme-on-surface));
}

.terms-document-view :deep(h1) {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
  color: rgb(var(--v-theme-on-surface));
  text-transform: none;
  margin: 0 0 1.5rem;
}

.terms-document-view :deep(h2) {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.4;
  color: rgb(var(--v-theme-on-surface));
  text-transform: none;
  margin: 2rem 0 0.75rem;
}

.terms-document-view :deep(h3) {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  color: rgb(var(--v-theme-on-surface));
  text-transform: none;
  margin: 1.5rem 0 0.5rem;
}

.terms-document-view :deep(p) {
  margin: 0 0 1rem;
  text-align: left;
}

.terms-document-view :deep(ul),
.terms-document-view :deep(ol) {
  padding-left: 1.5rem;
  margin: 0 0 1rem;
}

.terms-document-view :deep(li + li) {
  margin-top: 0.375rem;
}

.terms-document-view :deep(strong) {
  font-weight: 600;
}

.terms-document-view :deep(em) {
  font-style: italic;
}

.terms-document-view :deep(a) {
  color: rgb(var(--v-theme-secondary));
  text-decoration: underline;
}

.terms-document-view :deep(blockquote) {
  border-left: 3px solid rgb(var(--v-theme-on-surface) / 0.12);
  padding-left: 1rem;
  margin: 1rem 0;
  color: rgb(var(--v-theme-on-surface) / 0.6);
  font-style: italic;
}

.terms-document-view :deep(hr) {
  border: none;
  border-top: 1px solid rgb(var(--v-theme-on-surface) / 0.08);
  margin: 2rem 0;
}
</style>
